/**
 * The prototype's shell: rail, top bar, content column, page switch.
 *
 * Mounted by `routes/styleguide-verification.tsx` on a bare canvas — no product
 * chrome — so the layout below is the whole proposal, not a panel inside someone
 * else's frame.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import "./theme.css";
import { ME, TENANT, TENANTS, actor, fmtPeriod } from "./data";
import {
  BASE,
  NAV,
  activeNavPage,
  groupFor,
  href,
  navItemFor,
  resolveRoute,
  type PageId,
} from "./router";
import { Icon, Toasts } from "./ui";
import { StoreProvider, myQueue, openFindings, useStore } from "./store";
import { OverviewPage } from "./pages-overview";
import { DesignPage, GuidePage } from "./pages-reference";
// The buyable-product surfaces. Unlike every other page file here, these import
// shipped engine modules from `@excellent/core/verification-*` rather than the
// fixture — the prototype is a view over real product code, not a mock of it.
import {
  ApiSurfacePage,
  BuyablePage,
  IntakePage,
  PosturePage,
  TrustPage,
} from "./pages-buyable";
import {
  FindingDetailPage,
  FindingsPage,
  QueuePage,
  RunDetailPage,
  RunsPage,
} from "./pages-decide";
import {
  ControlDetailPage,
  ControlsPage,
  PoliciesPage,
  PolicyDetailPage,
  WorkflowDetailPage,
  WorkflowsPage,
} from "./pages-define";
import { ArtifactDetailPage, ConnectorsPage, EvidencePage } from "./pages-evidence";
import {
  AuditRoomPage,
  PackDetailPage,
  PacksPage,
  ReceiptDetailPage,
  ReceiptsPage,
} from "./pages-prove";
import { RuntimePage, ScorecardPage, WorkersPage } from "./pages-operate";

/* ══════════════════════════════════════════════════════════════════════════
   ENTRY
   ══════════════════════════════════════════════════════════════════════════ */

export function VerificationPrototype() {
  useLightCanvas();
  return (
    <StoreProvider>
      <Shell />
    </StoreProvider>
  );
}

/**
 * Force the document into light mode while the prototype is mounted, and put it
 * back on the way out.
 *
 * `client/index.html` ships `class="indigo-dark dark"` on `<html>` from the first
 * byte, deliberately, to avoid a theme flash. Nothing in `theme.css` depends on
 * this having run — every colour there is a literal scoped under `.vp` — but three
 * things live OUTSIDE that scope and would still render dark: the `color-scheme`
 * the browser uses for scrollbars and form controls, the area behind an
 * over-scroll bounce, and anything the host paints on `<body>`.
 *
 * Restoring on unmount matters: this is one route inside a running SPA, so
 * navigating away must not leave the product in the wrong theme.
 */
function useLightCanvas() {
  useEffect(() => {
    const root = document.documentElement;
    const had = { dark: root.classList.contains("dark"), indigo: root.classList.contains("indigo-dark") };
    root.classList.remove("dark", "indigo-dark");
    const priorScheme = root.style.colorScheme;
    root.style.colorScheme = "light";
    return () => {
      if (had.dark) root.classList.add("dark");
      if (had.indigo) root.classList.add("indigo-dark");
      root.style.colorScheme = priorScheme;
    };
  }, []);
}

/* ══════════════════════════════════════════════════════════════════════════
   SHELL
   ══════════════════════════════════════════════════════════════════════════ */

function Shell() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const store = useStore();

  const splat = useMemo(() => {
    if (!pathname.startsWith(BASE)) return "";
    return pathname.slice(BASE.length);
  }, [pathname]);

  const route = useMemo(() => resolveRoute(splat), [splat]);

  // Rail disclosure state. Only meaningful below the 820px breakpoint, where the
  // rail becomes an overlay — above it the rail is always visible and the toggle
  // is `display: none`, so this stays false and costs nothing.
  const [railOpen, setRailOpen] = useState(false);

  const go = (to: string) => {
    setRailOpen(false);
    void navigate({ to, resetScroll: true });
  };

  // Scroll the content column back to the top on navigation. The scroll container
  // is our own div rather than the window, so TanStack's `resetScroll` cannot
  // reach it — without this, opening a finding from halfway down the queue lands
  // you halfway down the finding.
  useEffect(() => {
    document.querySelector(".vp-scroll")?.scrollTo({ top: 0 });
  }, [route.page, route.id]);

  return (
    <div className="vp">
      <a className="vp-skip" href="#vp-content">
        Skip to content
      </a>
      <Rail current={route.page} onNavigate={go} open={railOpen} />
      {railOpen ? (
        <button
          type="button"
          className="vp-rail-scrim"
          aria-label="Close navigation"
          onClick={() => setRailOpen(false)}
        />
      ) : null}
      <div className="vp-main">
        <TopBar
          route={route}
          onNavigate={go}
          railOpen={railOpen}
          onToggleRail={() => setRailOpen((o) => !o)}
        />
        <div className="vp-scroll" id="vp-content" tabIndex={-1}>
          <Page page={route.page} id={route.id} raw={route.raw} onNavigate={go} />
        </div>
      </div>
      <Toasts items={store.toasts} />
    </div>
  );
}

/* ── Rail ────────────────────────────────────────────────────────────────── */

function Rail({
  current,
  onNavigate,
  open,
}: {
  current: PageId;
  onNavigate: (to: string) => void;
  open: boolean;
}) {
  const store = useStore();
  const active = activeNavPage(current);

  // Counts are recomputed from live store state rather than read from `data.ts`,
  // so recording a decision visibly drops the badge. A rail whose numbers are
  // frozen fixtures is the fastest way to make a prototype feel fake.
  const counts: Partial<Record<PageId, number>> = {
    queue: myQueue(store.findings).length,
    findings: openFindings(store.findings).length,
    workflows: store.candidates.filter((c) => c.status === "needs-oracle" || c.status === "draft").length,
  };

  return (
    <nav className="vp-rail" aria-label="Verification" data-open={open ? "true" : undefined}>
      <div className="vp-rail-head">
        <button
          type="button"
          className="vp-brand"
          onClick={() => onNavigate(href())}
          title="Verification overview"
        >
          <span className="vp-brand-mark" aria-hidden="true">
            E
          </span>
          <span>
            <span className="vp-brand-name">Excellent</span>
            <span className="vp-brand-sub">Verification</span>
          </span>
        </button>

        <TenantSwitcher />
      </div>

      <div className="vp-nav">
        {NAV.map((group, gi) => (
          <div className="vp-nav-group" key={group.label ?? `g${gi}`}>
            {group.label ? <div className="vp-nav-group-label">{group.label}</div> : null}
            {group.items.map((item) => {
              const count = counts[item.page] ?? item.count;
              return (
                <button
                  key={item.page}
                  type="button"
                  className="vp-nav-item"
                  data-active={active === item.page ? "true" : undefined}
                  aria-current={active === item.page ? "page" : undefined}
                  onClick={() => onNavigate(href(...(item.path ? [item.path] : [])))}
                  title={item.purpose}
                >
                  <span className="vp-nav-icon">
                    <Icon name={item.icon} size={15} />
                  </span>
                  <span className="vp-nav-label">{item.label}</span>
                  {count ? (
                    <span className="vp-nav-count" data-tone={item.countTone}>
                      {count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="vp-rail-foot">
        <button type="button" className="vp-user" title={`${ME.email} · role: ${ME.role}`}>
          <span className="vp-avatar" aria-hidden="true">
            {ME.initials}
          </span>
          <span>
            <span className="vp-user-name">{ME.name}</span>
            {/* The role is on screen at all times on purpose: what you may decide
                depends on it, and "why is this button missing?" is otherwise the
                most common confusion in a role-gated review tool. */}
            <span className="vp-user-role">Reviewer · can decide, cannot activate policy</span>
          </span>
        </button>
      </div>
    </nav>
  );
}

/**
 * Tenant switcher.
 *
 * Present because this is a multi-tenant product and every screen's numbers are
 * scoped to one tenant — a compliance tool that leaves you guessing whose data you
 * are reading is a serious defect, not a rough edge. The second tenant is real in
 * the fixture (a HIPAA workspace on a local-offline runtime) so the switch shows
 * that framework and runtime mode travel with the tenant.
 */
function TenantSwitcher() {
  const [open, setOpen] = useState(false);
  const store = useStore();

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className="vp-tenant"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="vp-avatar" aria-hidden="true">
          NS
        </span>
        <span className="vp-tenant-name">
          <span>{TENANT.name}</span>
          <span className="vp-tenant-meta">
            {TENANT.framework} · {fmtPeriod(TENANT.periodStart, TENANT.periodEnd)}
          </span>
        </span>
        <span className="vp-tenant-caret" aria-hidden="true">
          <Icon name="chevron-down" size={13} />
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          className="rounded-md"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "var(--overlay)",
            border: "1px solid var(--line)",
            boxShadow: "var(--shadow-pop)",
            zIndex: 20,
            padding: "calc(var(--spacing) * 1)",
          }}
        >
          {TENANTS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="option"
              aria-selected={t.id === TENANT.id}
              className="vp-nav-item"
              onClick={() => {
                setOpen(false);
                if (t.id !== TENANT.id) {
                  store.toast(`Prototype: only ${TENANT.name} is populated with fixture data.`);
                }
              }}
            >
              <span className="vp-nav-label">
                {t.name}
                <span style={{ display: "block", fontSize: "var(--type-subtext-size)", color: "var(--ink-3)" }}>
                  {t.framework} · {t.runtimeMode}
                </span>
              </span>
              {t.id === TENANT.id ? (
                <span style={{ color: "var(--accent)" }}>
                  <Icon name="check" size={14} />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ── Top bar ─────────────────────────────────────────────────────────────── */

function TopBar({
  route,
  onNavigate,
  railOpen,
  onToggleRail,
}: {
  route: { page: PageId; id?: string };
  onNavigate: (to: string) => void;
  railOpen: boolean;
  onToggleRail: () => void;
}) {
  const item = navItemFor(route.page);
  const group = groupFor(route.page);
  const isDetail = Boolean(route.id);

  return (
    <div className="vp-topbar">
      {/* Only visible below 820px, where the rail collapses to an overlay. */}
      <button
        type="button"
        className="vp-nav-toggle"
        onClick={onToggleRail}
        aria-expanded={railOpen}
        aria-label={railOpen ? "Close navigation" : "Open navigation"}
      >
        <Icon name={railOpen ? "x" : "menu"} size={16} />
      </button>

      <nav className="vp-crumbs" aria-label="Breadcrumb">
        <button type="button" className="vp-crumb" onClick={() => onNavigate(href())}>
          {TENANT.name}
        </button>
        {group ? (
          <>
            <span className="vp-crumb-sep" aria-hidden="true">
              /
            </span>
            <span className="vp-crumb" style={{ cursor: "default" }}>
              {group}
            </span>
          </>
        ) : null}
        {item ? (
          <>
            <span className="vp-crumb-sep" aria-hidden="true">
              /
            </span>
            {isDetail ? (
              <button
                type="button"
                className="vp-crumb"
                onClick={() => onNavigate(href(...(item.path ? [item.path] : [])))}
              >
                {item.label}
              </button>
            ) : (
              <span className="vp-crumb-current">{item.label}</span>
            )}
          </>
        ) : null}
        {isDetail ? (
          <>
            <span className="vp-crumb-sep" aria-hidden="true">
              /
            </span>
            <span className="vp-crumb-current">{route.id}</span>
          </>
        ) : null}
      </nav>

      {/* The prototype flag rides in the chrome, on every page, so a screenshot of
          any single screen cannot be mistaken for the shipped product. */}
      <span className="vp-proto-flag" title="Design prototype — fixture data, not a live workspace">
        Prototype
      </span>
    </div>
  );
}

/* ── Page switch ─────────────────────────────────────────────────────────── */

function Page({
  page,
  id,
  raw,
  onNavigate,
}: {
  page: PageId;
  id?: string;
  raw: string;
  onNavigate: (to: string) => void;
}) {
  switch (page) {
    case "overview":
      return <OverviewPage onNavigate={onNavigate} />;
    case "queue":
      return <QueuePage onNavigate={onNavigate} />;
    case "findings":
      return <FindingsPage onNavigate={onNavigate} />;
    case "finding":
      return <FindingDetailPage id={id ?? ""} onNavigate={onNavigate} />;
    case "runs":
      return <RunsPage onNavigate={onNavigate} />;
    case "run":
      return <RunDetailPage id={id ?? ""} onNavigate={onNavigate} />;
    case "workflows":
      return <WorkflowsPage onNavigate={onNavigate} />;
    case "workflow":
      return <WorkflowDetailPage id={id ?? ""} onNavigate={onNavigate} />;
    case "controls":
      return <ControlsPage onNavigate={onNavigate} />;
    case "control":
      return <ControlDetailPage id={id ?? ""} onNavigate={onNavigate} />;
    case "policies":
      return <PoliciesPage onNavigate={onNavigate} />;
    case "policy":
      return <PolicyDetailPage id={id ?? ""} onNavigate={onNavigate} />;
    case "evidence":
      return <EvidencePage onNavigate={onNavigate} />;
    case "artifact":
      return <ArtifactDetailPage id={id ?? ""} onNavigate={onNavigate} />;
    case "connectors":
      return <ConnectorsPage onNavigate={onNavigate} />;
    case "receipts":
      return <ReceiptsPage onNavigate={onNavigate} />;
    case "receipt":
      return <ReceiptDetailPage id={id ?? ""} onNavigate={onNavigate} />;
    case "packs":
      return <PacksPage onNavigate={onNavigate} />;
    case "pack":
      return <PackDetailPage id={id ?? ""} onNavigate={onNavigate} />;
    case "audit-room":
      return <AuditRoomPage onNavigate={onNavigate} />;
    case "workers":
      return <WorkersPage onNavigate={onNavigate} />;
    case "scorecard":
      return <ScorecardPage onNavigate={onNavigate} />;
    case "runtime":
      return <RuntimePage />;
    // The API page is now engine-backed: it renders the real served surface from
    // `describeVerificationApiSurface`, so the withheld operation on screen is
    // withheld by the same code that refuses the route.
    case "api":
      return <ApiSurfacePage />;
    case "workflow-new":
      return <IntakePage onNavigate={onNavigate} />;
    case "trust":
      return <TrustPage />;
    case "posture":
      return <PosturePage />;
    case "buyable":
      return <BuyablePage />;
    case "guide":
      return <GuidePage onNavigate={onNavigate} />;
    case "design":
      return <DesignPage />;
    case "not-found":
      return <NotFound raw={raw} onNavigate={onNavigate} />;
  }
}

function NotFound({ raw, onNavigate }: { raw: string; onNavigate: (to: string) => void }) {
  return (
    <div className="vp-page">
      <div className="vp-card">
        <div className="vp-empty">
          <div className="vp-empty-title">No prototype page at /{raw}</div>
          <p className="vp-empty-body">
            This prototype resolves its own paths. Every page in the rail is deep-linkable; anything else
            lands here rather than silently redirecting, so a broken link in a review comment is visible.
          </p>
          <button type="button" className="vp-btn" data-variant="primary" onClick={() => onNavigate(href())}>
            Go to Overview
          </button>
        </div>
      </div>
    </div>
  );
}

/** Named export used by the guide to attribute the signed-in reviewer. */
export const SIGNED_IN = actor(ME.id);
